<template>
  <div class="saml-verify-container">
    <div class="text-center">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-3">{{ loadingMessage }}</p>
      <p v-if="error" class="text-danger mt-2">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { authHandler } from '@/auth/mixins/auth-handler';
import { getDomain } from '@/store/auth/auth.store';
import { AUTH_STATE_COOKIE } from '@/store/auth/auth.store.constants';

export default {
  name: 'SAMLVerify',

  mixins: [authHandler],

  inject: {
    authApiUrl: {
      default: process.env.VUE_APP_AUTH_API_URL || 'https://auth-api.nectios.com/auth'
    }
  },

  data() {
    return {
      error: null,
      isLoading: true,
      loadingMessage: 'Verificando autenticación...'
    };
  },

  computed: {
    communitySlug() {
      return this.$route.params.communityId;
    }
  },

  async created() {
    await this.verifySAML();
  },

  methods: {
    async verifySAML() {
      try {
        this.isLoading = true;
        this.error = null;
        this.loadingMessage = 'Verificando autenticación...';

        console.log('SAMLVerify: Starting SAML session verification');
        console.log('SAMLVerify: Community slug:', this.communitySlug);
        console.log('SAMLVerify: Auth API URL:', this.authApiUrl);

        // Build the endpoint URL - match the pattern used in SAMLCallback.vue
        // Use the same logic as SAMLCallback to ensure consistency
        let baseUrl = process.env.VUE_APP_AUTH_API_URL || 'https://auth-api.nectios.com';
        
        // Remove trailing slash if present
        if (baseUrl.endsWith('/')) {
          baseUrl = baseUrl.slice(0, -1);
        }
        
        // Remove '/auth' suffix if present (SAMLCallback removes it)
        if (baseUrl.endsWith('/auth')) {
          baseUrl = baseUrl.slice(0, -5);
        }
        
        // Construct endpoint - same pattern as SAMLCallback.vue
        const endpoint = `${baseUrl}/v1/auth/verify-saml-session`;

        console.log('SAMLVerify: Calling endpoint:', endpoint);

        // CRITICAL: Include credentials to send httpOnly cookies (frontendToken, backendToken)
        // These cookies were set by the backend during SAML callback
        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'include', // CRITICAL: Include cookies in the request
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            communitySlug: this.communitySlug
          })
        });

        console.log('SAMLVerify: Response status:', response.status);

        // Handle non-OK responses
        if (!response.ok) {
          if (response.status === 401) {
            this.error = 'La autenticación falló. Por favor, intenta iniciar sesión nuevamente.';
            console.error('SAMLVerify: Authentication failed - cookies not found or invalid');
            
            // Wait a bit before redirecting to show error message
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Redirect to login page
            const loginPath = this.communitySlug 
              ? `/${this.communitySlug}/auth/login`
              : '/login';
            this.$router.push({ path: loginPath });
            return;
          }

          // Try to parse error response
          let errorMessage = 'Error al verificar la sesión';
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (e) {
            errorMessage = `Error del servidor: ${response.status}`;
          }

          this.error = errorMessage;
          console.error('SAMLVerify: Verification failed:', response.status, errorMessage);
          
          // Wait a bit before redirecting
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          const loginPath = this.communitySlug 
            ? `/${this.communitySlug}/auth/login`
            : '/login';
          this.$router.push({ path: loginPath });
          return;
        }

        // Parse successful response
        const data = await response.json();
        console.log('SAMLVerify: Verification response:', data);

        // Verify response structure
        if (data.message !== 'OK') {
          this.error = data.error || data.message || 'Respuesta inesperada del servidor';
          console.error('SAMLVerify: Unexpected response format:', data);
          
          await new Promise(resolve => setTimeout(resolve, 2000));
          const loginPath = this.communitySlug 
            ? `/${this.communitySlug}/auth/login`
            : '/login';
          this.$router.push({ path: loginPath });
          return;
        }

        console.log('SAMLVerify: Authentication successful');
        console.log('SAMLVerify: User info:', data.user);
        console.log('SAMLVerify: Session info:', data.sessionInfo);

        // Store user and session info in Vuex (same as normal login flow)
        if (data.user) {
          this.$store.commit('user-data/setLoggedUser', {
            user: data.user,
            member: data.member || {},
            roles: data.roles || []
          });
          console.log('SAMLVerify: User data stored in Vuex');
        }

        if (data.sessionInfo) {
          this.$store.dispatch('session/updateSessionInfo', data.sessionInfo);
          console.log('SAMLVerify: Session info stored in Vuex');
        }

        // Set auth state cookie (non-httpOnly, just to indicate authenticated state)
        // This is used by the app to know the user is authenticated
        Vue.$cookies.set(AUTH_STATE_COOKIE, 'authenticated', {
          httpOnly: false,
          domain: getDomain(),
          path: '/',
          secure: window.location.protocol === 'https:',
          sameSite: 'Lax'
        });
        console.log('SAMLVerify: Auth state cookie set');

        // Push Nexos dataLayer if needed (for analytics)
        if (data.user) {
          this.pushNexosDataLayer(data, this.$store);
          console.log('SAMLVerify: Nexos dataLayer pushed (if applicable)');
        }

        // Use the auth handler's handleAuthSuccess method for proper flow
        // This handles security warnings, session conflicts, and redirection
        this.loadingMessage = 'Iniciando sesión...';
        
        // Wait a moment for cookies to be processed by the browser
        await new Promise(resolve => setTimeout(resolve, 100));

        // Use handleAuthSuccess which handles the complete login flow
        // It will handle session info, security warnings, and redirection
        await this.handleAuthSuccess(data);

        console.log('SAMLVerify: Authentication flow completed successfully');
      } catch (error) {
        console.error('SAMLVerify: Error during verification:', error);
        this.error = error.message || 'Error inesperado al verificar la sesión';
        this.isLoading = false;

        // Wait a bit before redirecting
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        const loginPath = this.communitySlug 
          ? `/${this.communitySlug}/auth/login`
          : '/login';
        this.$router.push({ path: loginPath });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.saml-verify-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3em;
}

.text-primary {
  color: var(--primary-color, #007bff) !important;
}

.text-danger {
  color: #dc3545;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}
</style>
